import type { IMyTodo, IMyTodoListRow } from './my-todo.types';

function completionRatio(done: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.round((done / total) * 10000) / 100;
}

type AssigneeGroupLike = { tasks?: IMyTodo[] };

/**
 * Flattens legacy API rows (`assigneeGroups`, top-level `tasks`) and new rows
 * (`hasExpandData`, `expandData`, `task`) into a single {@link IMyTodoListRow} shape.
 */
export function normalizeMyTodoListRows(raw: unknown): IMyTodoListRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(normalizeMyTodoListRow).filter((row): row is IMyTodoListRow => row != null);
}

function mergeTasksFromPayload(r: Record<string, unknown>): IMyTodo[] {
  const assigneeGroups = r.assigneeGroups as AssigneeGroupLike[] | undefined;
  const fromAssignees = assigneeGroups?.flatMap((ag) => ag.tasks ?? []) ?? [];
  const tasksProp = Array.isArray(r.tasks) ? (r.tasks as IMyTodo[]) : [];
  const expandDataProp = Array.isArray(r.expandData) ? (r.expandData as IMyTodo[]) : [];
  const explicitTask = r.task && typeof r.task === 'object' ? [r.task as IMyTodo] : [];
  if (expandDataProp.length > 0) {
    return expandDataProp;
  }
  if (tasksProp.length > 0) {
    return tasksProp;
  }
  if (fromAssignees.length > 0) {
    return fromAssignees;
  }
  if (explicitTask.length > 0) {
    return explicitTask;
  }
  return [];
}

function resolveDevOrderForRow(rowDev: IMyTodoListRow['devOrderId'], firstTask: IMyTodo | undefined): IMyTodoListRow['devOrderId'] {
  if (rowDev) {
    return rowDev;
  }
  if (firstTask && typeof firstTask.devOrderId === 'object' && firstTask.devOrderId) {
    const devOrderRef = firstTask.devOrderId as { _id?: string; image?: string; number?: string };
    if (devOrderRef._id) {
      return devOrderRef as { _id: string; image?: string; number?: string };
    }
  }
  return null;
}

function normalizeMyTodoListRow(item: unknown): IMyTodoListRow | null {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const record = item as Record<string, unknown>;

  const groupKey = String(record.groupKey ?? record._id ?? '');
  if (!groupKey) {
    return null;
  }

  const mergedTasks = mergeTasksFromPayload(record);

  const totalTasks = typeof record.totalTasks === 'number' ? record.totalTasks : mergedTasks.length;
  const doneTasks = typeof record.doneTasks === 'number' ? record.doneTasks : mergedTasks.filter((t) => t.status === 'Done').length;

  const completionPercent = typeof record.completionPercent === 'number' ? record.completionPercent : completionRatio(doneTasks, totalTasks);

  const devOrderId = resolveDevOrderForRow(record.devOrderId as IMyTodoListRow['devOrderId'], mergedTasks[0]);

  const multi = totalTasks > 1;

  return {
    _id: String(record._id ?? groupKey),
    groupKey,
    ...(devOrderId !== undefined ? { devOrderId } : {}),
    totalTasks,
    doneTasks,
    completionPercent,
    hasExpandData: multi,
    expandData: multi ? mergedTasks : [],
    ...(!multi && mergedTasks[0] ? { task: mergedTasks[0] } : {}),
  };
}
